/**
 * ConfirmModal — shared confirm/info dialog (Promote's "User Created" panel,
 * Demote's confirm). Same absolute-overlay shell as
 * team-management-field-select-dialog.tsx, NOT `@pimm/base`/native-base
 * `Modal` — avoids nesting a Modal inside MemberProfileModal (itself a
 * Modal), which creates competing focus traps.
 */
import type { ReactNode } from 'react';
import { Box, HStack, Pressable, VStack } from 'native-base';
import { Button, Text } from '@pimm/base';

type ConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  titleIcon?: ReactNode;
  body?: string;
  children?: ReactNode;
  hideCancel?: boolean;
  cancelLabel?: string;
  confirmLabel: string;
  footerLeft?: ReactNode;
  width?: number;
  // 'danger' reddens the Confirm button for destructive-leaning actions
  // (e.g. Demote, which disables a login) — default styling otherwise.
  tone?: 'default' | 'danger';
};

export const InfoCard = ({ label, children }: { label: string; children: ReactNode }) => (
  <VStack flex={1} space={0.5} borderWidth={1} borderColor="gray.200" rounded="md" p={3}>
    <Text size="xs" fontWeight={500} color="gray.500">
      {label}
    </Text>
    <Text size="sm" fontWeight={600} color="gray.900">
      {children}
    </Text>
  </VStack>
);

export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  titleIcon,
  body,
  children,
  hideCancel,
  cancelLabel,
  confirmLabel,
  footerLeft,
  width = 480,
  tone = 'default',
}: ConfirmModalProps) => {
  if (!isOpen) return null;

  return (
    <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={50}>
      <Pressable position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0,0,0,0.5)" onPress={onClose} />
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        alignItems="center"
        justifyContent="center"
        // @ts-ignore — pointerEvents is a valid RNW prop
        pointerEvents="box-none"
      >
        <Box bg="white" rounded="xl" shadow={9} w={`${width}px`} maxW="92%" maxH="84%" overflow="hidden">
          <HStack px={5} pt={4} pb={3} borderBottomWidth={1} borderColor="gray.200" alignItems="center" space={2}>
            {titleIcon}
            <Text size="md" fontWeight={600} color="gray.900">
              {title}
            </Text>
          </HStack>

          <VStack px={5} py={4} space={3}>
            {!!body && (
              <Text size="sm" color="gray.700">
                {body}
              </Text>
            )}
            {children}
          </VStack>

          <HStack alignItems="center" justifyContent="space-between" px={5} py={4} borderTopWidth={1} borderColor="gray.200">
            <Box>{footerLeft}</Box>
            <HStack space={3}>
              {!hideCancel && (
                <Pressable onPress={onClose}>
                  <Box px={4} py={2} rounded="lg" borderWidth={1} borderColor="gray.300" bg="white" shadow={1}>
                    <Text size="sm" fontWeight={600} color="gray.700">
                      {cancelLabel}
                    </Text>
                  </Box>
                </Pressable>
              )}
              <Button size="sm" onPress={onConfirm} {...(tone === 'danger' ? { bg: 'error.600', _text: { color: 'white' } } : {})}>
                {confirmLabel}
              </Button>
            </HStack>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

export default ConfirmModal;
